import { collection, addDoc, onSnapshot, query, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Subscribe to categories collection, sorted on client to handle missing createdAt
// Prefetches once immediately; falls back to periodic fetch if realtime listener errors
export function subscribeCategories(onUpdate, onError, onStatus) {
  const q = query(collection(db, 'categories'));
  let poller = null;

  const log = (evt) => {
    try { if (typeof onStatus === 'function') onStatus({ ...evt, at: Date.now() }); } catch {}
  };

  const deliver = (docs, source) => {
    let list = docs.map((d) => ({ id: d.id, ...d.data() }));
    // Sort by createdAt desc when present, then by name asc
    list.sort((a, b) => {
      const ac = a.createdAt ?? 0;
      const bc = b.createdAt ?? 0;
      if (ac !== bc) return bc - ac; // newer first
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      return an.localeCompare(bn);
    });
    log({ type: source || 'unknown', count: list.length });
    onUpdate(list);
  };

  // One-time prefetch to show data even if realtime has issues
  (async () => {
    try {
      const snapOnce = await getDocs(q);
      deliver(snapOnce.docs, 'prefetch');
    } catch (e) {
      log({ type: 'error', error: e?.message, code: e?.code });
      if (onError) onError(e);
    }
  })();

  const startPolling = async () => {
    try {
      const snapOnce = await getDocs(q);
      deliver(snapOnce.docs, 'poll');
    } catch (e) {
      log({ type: 'error', error: e?.message, code: e?.code });
      if (onError) onError(e);
    }
    poller = setInterval(async () => {
      try {
        const snap = await getDocs(q);
        deliver(snap.docs, 'poll');
      } catch (e) {
        log({ type: 'error', error: e?.message, code: e?.code });
        if (onError) onError(e);
      }
    }, 10000);
  };

  const unsubRealtime = onSnapshot(
    q,
    (snap) => {
      if (poller) { clearInterval(poller); poller = null; }
      deliver(snap.docs, 'realtime');
    },
    (err) => {
      log({ type: 'error', error: err?.message, code: err?.code });
      if (onError) onError(err);
      // Fallback to polling when realtime channel fails
      startPolling();
    }
  );

  return () => {
    try { unsubRealtime(); } catch {}
    if (poller) clearInterval(poller);
    log({ type: 'unsub' });
  };
}

// Create a category document
// Expects: { name, color, icon, description, premium }
export async function createCategory({ name, color, icon, description, premium }) {
  return await addDoc(collection(db, 'categories'), {
    name,
    color,
    icon,
    description: typeof description === 'string' ? description : '',
    premium: typeof premium === 'boolean' ? premium : false,
    createdAt: Date.now(),
  });
}
export async function updateCategory(id, { name, color, icon, description, premium }) {
  const payload = { name, color, icon };
  if (typeof description === 'string') payload.description = description;
  if (typeof premium === 'boolean') payload.premium = premium;
  return await updateDoc(doc(db, 'categories', id), payload);
}
export async function deleteCategory(id) {
  return await deleteDoc(doc(db, 'categories', id));
}